// Canonical number / currency formatters for the entire app.
// Use these — never write `new Intl.NumberFormat(...)` directly elsewhere.

/**
 * Format an amount as Indian Rupees.
 * `compact=true` → "1.2L" / "1.5Cr" for charts/tables (Indian numbering).
 * `compact=false` → "₹1,23,456" (full).
 *
 * Always uses 'en-IN' locale and 'INR' currency.
 * Returns '-' for null/undefined; '₹0' for 0.
 */
export const formatCurrency = (amount, compact = false) => {
  if (amount === null || amount === undefined || amount === '') return '-';
  const n = Number(amount);
  if (!isFinite(n)) return '-';

  if (compact) {
    const abs = Math.abs(n);
    if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
    if (abs >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
    if (abs >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
    return `₹${Math.round(n)}`;
  }

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
};

/**
 * Format a number with Indian thousands separators (no currency symbol).
 * "1234567" → "12,34,567"
 */
export const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined || value === '') return '-';
  const n = Number(value);
  if (!isFinite(n)) return '-';
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
};

/**
 * Format a percentage. `0.123` (decimal) or `12.3` (percent) → "12.3%".
 * Auto-detects: if value <= 1 and not 0, treats as decimal; else as percent.
 */
export const formatPercent = (value, decimals = 1) => {
  if (value === null || value === undefined || value === '') return '-';
  const n = Number(value);
  if (!isFinite(n)) return '-';
  const pct = (Math.abs(n) <= 1 && n !== 0) ? n * 100 : n;
  return `${pct.toFixed(decimals)}%`;
};

/**
 * Format a file size in bytes → "12.3 KB" / "1.5 MB" / "2 GB".
 */
export const formatFileSize = (bytes) => {
  if (bytes === null || bytes === undefined) return '-';
  const n = Number(bytes);
  if (!isFinite(n) || n < 0) return '-';
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
};

export default { formatCurrency, formatNumber, formatPercent, formatFileSize };
